"use client";

import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { CreateAction } from '@/src/components/manage/actions/CreateAction'
import { DeleteAction } from '@/src/components/manage/actions/DeleteAction'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { Badge } from '@/src/components/ui/badge'
import { EnabledAction } from '@/src/components/manage/actions/EnabledAction'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { ViewAction } from '@/src/components/manage/actions/ViewAction'
import { listGlobalOperationTypes, deleteOperationType, updateOperationType } from '@/src/api/operation-type'
import { toast } from 'sonner'
import { useTranslation } from '@/src/i18n/client'
import { DateTimeText } from '@/src/components/ui/date-time'
import type { FormSchemaDefinition, LocalizedString } from '@/shared/schemas/operation-data.zod'

export const Route = createFileRoute('/_manage/manage/admin/operations/types')({
  component: AdminOperationTypesPage,
})

interface OperationTypeRow {
  id: string
  name: string
  labels: LocalizedString
  description?: LocalizedString | null
  icon?: string | null
  version: number
  formSchema: FormSchemaDefinition
  enabled: boolean
  deletedAt?: string | null
  createdAt: string
  updatedAt: string
  _count?: {
    operations: number
    clones: number
  }
}

function AdminOperationTypesPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedItems, setSelectedItems] = useState<string[]>([])

  const { data: operationTypes = [], isLoading, error } = useQuery({
    queryKey: ['operationTypes', 'global'],
    queryFn: () => listGlobalOperationTypes({ data: { includeDisabled: true } }),
  })

  const deleteMutation = useMutation<{ success: boolean; deleted?: boolean; disabled?: boolean }, unknown, { id: string; hardDelete: boolean }>({
    mutationFn: ({ id, hardDelete }) => deleteOperationType({ data: { id, hardDelete } }),
    onSuccess: (result) => {
      if (result.deleted) {
        toast.success('Operation type deleted')
      } else {
        toast.success('Operation type disabled')
      }
      queryClient.invalidateQueries({ queryKey: ['operationTypes'] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete')
    },
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => {
          const opType = (operationTypes as OperationTypeRow[]).find((t) => t.id === id)
          const hardDelete = opType?._count?.operations === 0
          return deleteOperationType({ data: { id, hardDelete } })
        })
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) throw new Error(`Failed to delete ${failed} of ${ids.length} items`)
      return results
    },
    onSuccess: (_, ids) => {
      toast.success(`${ids.length} operation type(s) processed`)
      setSelectedItems([])
      queryClient.invalidateQueries({ queryKey: ['operationTypes'] })
    },
    onError: () => {
      toast.error('Bulk operation failed')
    },
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateOperationType({ data: { id, enabled } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationTypes'] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update status')
    },
  })

  // navigation to the new type route is handled by CreateAction component

  const columns: ColumnConfig<OperationTypeRow>[] = [
    {
      key: 'name',
      label: 'Name',
      primary: true,
      sortable: false,
      render: (opType) => {
        const isDeleted = !!opType.deletedAt
        return (
          <div className="flex flex-col">
            <span
              className={
                isDeleted ? 'font-medium text-muted-foreground line-through' : 'font-medium'
              }
            >
              {opType.labels?.en || opType.name}
            </span>
          </div>
        )
      },
    },
    {
      key: 'version',
      label: 'Version',
      width: 'w-20',
      render: (opType) => (
        <Badge variant="outline" className="text-xs">
          v{opType.version}
        </Badge>
      ),
    },
    {
      key: 'fields',
      label: 'Fields',
      width: 'w-20',
      render: (opType) => (
        <span className="text-muted-foreground">
          {opType.formSchema?.fields?.length || 0}
        </span>
      ),
    },
    {
      key: 'usage',
      label: 'Usage',
      width: 'w-32',
      render: (opType) => (
        <div className="flex flex-col text-xs text-muted-foreground">
          <span>{opType._count?.operations || 0} operations</span>
          <span>{opType._count?.clones || 0} clones</span>
        </div>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      width: 'w-36',
      sortable: false,
      render: (opType) => <DateTimeText>{opType.updatedAt}</DateTimeText>,
    },
    {
      key: 'enabled',
      label: t('table.status'),
      width: 'w-24',
      align: 'right',
      render: (opType) => {
        if (opType.deletedAt) {
          return <Badge variant="destructive" className="text-xs">Deleted</Badge>
        }
        return (
          <EnabledAction
            enabled={opType.enabled}
            ariaLabel={opType.enabled ? 'Enabled' : 'Disabled'}
            onToggle={async (enabled) => {
              await toggleEnabledMutation.mutateAsync({ id: opType.id, enabled })
            }}
          />
        )
      },
    },
  ]

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar selectedCount={selectedItems.length}>
        <AdminToolbar.BulkActions>
          <DeleteAction
            itemName={`${selectedItems.length} operation type(s)`}
            itemType="operation types"
            buttonLabel="Delete"
            showText={true}
            variant="destructive"
            size="lg"
            onDelete={async () => {
              await bulkDeleteMutation.mutateAsync(selectedItems)
            }}
            isPending={bulkDeleteMutation.isPending}
            customDescription={`This will delete ${selectedItems.length} selected operation type(s). This action cannot be undone.`}
          />
        </AdminToolbar.BulkActions>

        <AdminToolbar.Left>
          <CreateAction
            to="/manage/admin/operations/types/new"
            label="New Operation Type"
            size="sm"
          />
        </AdminToolbar.Left>
      </AdminToolbar>

      <div className="mt-4">
        <DataTable<OperationTypeRow>
          data={operationTypes as OperationTypeRow[]}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage="No operation types defined yet. Create one to get started."
          selectable={true}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          actions={(opType) => (
            <>
              <ViewAction
                to={`/manage/admin/operations/types/${opType.id}`}
              />
              {!opType.deletedAt && (
                <>
                  <EditAction
                    to={`/manage/admin/operations/types/${opType.id}/edit`}
                  />
                  <DeleteAction
                    itemName={opType.labels?.en || opType.name}
                    itemType="operation type"
                    onDelete={async () => {
                      const hardDelete = (opType._count?.operations || 0) === 0
                      await deleteMutation.mutateAsync({ id: opType.id, hardDelete })
                    }}
                    isPending={deleteMutation.isPending}
                  />
                </>
              )}
            </>
          )}
        />
      </div>

    </div>
  )
}
